import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'lodash';

import { formatStateValue } from '../utils/contact';

const makeAdditionalLines = (lines, subtext) => {
  if (_.size(lines) >= 2) {
    return (
      <div className={classnames({ 'address__sub-text': subtext })}>
        {lines[0]}
        <br />
        {lines[1]}
      </div>
    );
  }

  if (_.size(lines) === 1) {
    return <div className={classnames({ 'address__sub-text': subtext })}>{lines[0]}</div>;
  }

  return null;
};

const makeAddressText = (parts, singleLine, subtext, firstSeparator) => {
  if (singleLine) {
    if (_.size(parts) > 1 && firstSeparator) {
      return `${parts[0]} ${firstSeparator} ${_.join(_.slice(parts, 1), ', ')}`;
    }
    return _.join(parts, ', ');
  }

  if (_.size(parts) === 1) {
    return parts[0];
  }

  if (_.size(parts) > 1) {
    return (
      <Fragment>
        {parts[0]}
        {makeAdditionalLines(_.slice(parts, 1), subtext)}
      </Fragment>
    );
  }

  return null;
};

const shouldInclude = (name, includeValues, address) =>
  _.includes(includeValues, name) && _.get(address, name);

const Address = props => {
  const {
    address,
    className,
    firstSeparator,
    includeValues,
    singleLine,
    subtext,
    tag: Tag,
  } = props;

  const parts = [];
  if (shouldInclude('name', includeValues, address)) {
    parts.push(address.name);
  } else if (shouldInclude('display', includeValues, address)) {
    parts.push(address.display);
  }

  if (shouldInclude('address1', includeValues, address)) {
    parts.push(address.address1);
  }

  if (shouldInclude('address2', includeValues, address)) {
    parts.push(address.address2);
  }

  if (
    shouldInclude('city', includeValues, address) &&
    shouldInclude('state', includeValues, address) &&
    shouldInclude('zipcode', includeValues, address)
  ) {
    parts.push(
      `${address.city}, ${formatStateValue(address.country, address.state)} ${address.zipcode}`,
    );
  } else if (
    shouldInclude('city', includeValues, address) &&
    shouldInclude('state', includeValues, address)
  ) {
    parts.push(`${address.city}, ${formatStateValue(address.country, address.state)}`);
  } else if (
    shouldInclude('city', includeValues, address) &&
    shouldInclude('zipcode', includeValues, address)
  ) {
    parts.push(`${address.city}, ${address.zipcode}`);
  } else if (
    shouldInclude('state', includeValues, address) &&
    shouldInclude('zipcode', includeValues, address)
  ) {
    parts.push(`${formatStateValue(address.country, address.state)}, ${address.zipcode}`);
  } else if (shouldInclude('city', includeValues, address)) {
    parts.push(address.city);
  } else if (shouldInclude('state', includeValues, address)) {
    parts.push(formatStateValue(address.country, address.state));
  } else if (shouldInclude('zipcode', includeValues, address)) {
    parts.push(address.zipcode);
  }

  return (
    <Tag className={classnames('address', className)}>
      {makeAddressText(parts, singleLine, subtext, firstSeparator)}
    </Tag>
  );
};

Address.propTypes = {
  address: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    display: PropTypes.string,
    name: PropTypes.string,
    state: PropTypes.string,
    zipcode: PropTypes.string,
  }),
  className: PropTypes.string,
  firstSeparator: PropTypes.string,
  includeValues: PropTypes.arrayOf(PropTypes.string),
  singleLine: PropTypes.bool,
  subtext: PropTypes.bool,
  tag: PropTypes.string,
};

Address.defaultProps = {
  includeValues: ['address1', 'city', 'state', 'zipcode'],
  firstSeparator: null,
  singleLine: false,
  subtext: false,
  tag: 'span',
};

export default Address;
